import { useEffect, useState, useCallback } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { isSolanaWallet } from "@dynamic-labs/solana";
import type { SolanaWallet } from "@dynamic-labs/solana-core";
import type { JupiterQuote, Token, TxPhase } from "@/types";
import { TokenAvatar } from "./TokenAvatar";
import { CloseIcon, LoaderIcon, CheckIcon, ArrowRightIcon, ExternalLinkIcon } from "./Icons";
import { GaslessFeeDisplay } from "./GaslessFeeDisplay";
import { fmtNumber, solscanTxUrl, DEFAULT_SLIPPAGE_BPS } from "@/lib/utils";
import { calcMinReceived, fetchJupiterOrder, formatPriceImpact } from "@/lib/jupiter";
import { getObliqFeePayer, signAndSendTransaction } from "@/lib/obliq";
import { deserializeVersionedTx, serializeTransaction } from "@/lib/solana";
import type { VersionedTransaction as Web3VersionedTransaction } from "@solana/web3.js";

interface ConfirmSwapDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	fromToken: Token | undefined;
	toToken: Token | undefined;
	fromAmount: number;
	quote: JupiterQuote | undefined;
	feeSol?: number;
	isFeeLoading?: boolean;
	onSuccess: () => void;
}

export function ConfirmSwapDialog({
	open,
	onOpenChange,
	fromToken,
	toToken,
	fromAmount,
	quote,
	feeSol,
	isFeeLoading = false,
	onSuccess,
}: ConfirmSwapDialogProps) {
	const [phase, setPhase] = useState<TxPhase>("review");
	const [txHash, setTxHash] = useState<string>("");
	const [error, setError] = useState<string>("");

	const { primaryWallet } = useDynamicContext();

	useEffect(() => {
		if (open) {
			setPhase("review");
			setTxHash("");
			setError("");
		}
	}, [open]);

	const toAmount = quote && toToken
		? Number(quote.outAmount) / 10 ** toToken.decimals
		: 0;

	const minReceived = quote && toToken
		? calcMinReceived(quote.outAmount, DEFAULT_SLIPPAGE_BPS, toToken.decimals)
		: null;

	const handleConfirm = useCallback(async () => {
		if (!fromToken || !toToken || !quote || !primaryWallet || !isSolanaWallet(primaryWallet)) return;
		setPhase("signing");
		setError("");

		try {
			const signer = await (primaryWallet as SolanaWallet).getSigner();
			const payer = await getObliqFeePayer();
			const rawAmount = BigInt(Math.round(fromAmount * 10 ** fromToken.decimals));
			const order = await fetchJupiterOrder(
				fromToken.address,
				toToken.address,
				rawAmount,
				primaryWallet.address,
				payer,
			);

			const versionedTx = deserializeVersionedTx(order.transaction);
			// Dynamic SDK bundles its own @solana/web3.js; types are structurally identical at runtime
			// biome-ignore lint/suspicious/noExplicitAny: duplicate @solana/web3.js versions
			const signedTx = await signer.signTransaction(versionedTx as never);
			setPhase("processing");

			const serialized = serializeTransaction(signedTx as unknown as Web3VersionedTransaction);
			const result = await signAndSendTransaction(serialized);

			setTxHash(result.signature);
			setPhase("success");
			onSuccess();
		} catch (err) {
			const msg = err instanceof Error ? err.message : "Transaction failed";
			setError(msg);
			setPhase("error");
		}
	}, [fromToken, toToken, quote, fromAmount, primaryWallet, onSuccess]);

	if (!fromToken || !toToken || !quote) return null;

	const isLoading = phase === "signing" || phase === "processing";

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="dialog-overlay" />
				<Dialog.Content className="dialog-content" style={{ maxWidth: 420 }}>
					<div className="dialog-head">
						<Dialog.Title className="dialog-title">Review swap</Dialog.Title>
						<Dialog.Close asChild>
							<button className="dialog-close" aria-label="Close" disabled={isLoading}>
								<CloseIcon />
							</button>
						</Dialog.Close>
					</div>

					<div className="confirm-body">
						<div className="confirm-flow">
							<div className="confirm-side">
								<span className="leg-label">From</span>
								<span className="leg-amount">{fmtNumber(fromAmount, 6)}</span>
								<span className="leg-tok">
									<TokenAvatar token={fromToken} size={22} />
									{fromToken.symbol} · Solana
								</span>
							</div>

							<div className="confirm-arrow">
								<ArrowRightIcon />
							</div>

							<div className="confirm-side">
								<span className="leg-label">To</span>
								<span className="leg-amount">{fmtNumber(toAmount, 6)}</span>
								<span className="leg-tok">
									<TokenAvatar token={toToken} size={22} />
									{toToken.symbol} · Solana
								</span>
							</div>
						</div>

						<div className="confirm-summary">
							<div className={`ico${phase === "error" ? " ico--error" : ""}`}>
								{isLoading && <LoaderIcon className="spin" />}
								{phase === "success" && <CheckIcon />}
								{phase === "review" && <ArrowRightIcon />}
								{phase === "error" && <span>!</span>}
							</div>
							<div className="text">
								<span className="title">
									{phase === "review" && `Swap ${fromToken.symbol} → ${toToken.symbol}`}
									{phase === "signing" && "Awaiting signature…"}
									{phase === "processing" && "Processing swap…"}
									{phase === "success" && "Swap successful!"}
									{phase === "error" && "Swap failed"}
								</span>
								<span className={`sub${phase === "error" ? " sub--error" : ""}`}>
									{phase === "review" && "Confirm in your wallet"}
									{phase === "signing" && "Approve in your wallet app"}
									{phase === "processing" && "Executing via OBLIQ + Jupiter…"}
									{phase === "success" && txHash && (
										<a
											href={solscanTxUrl(txHash)}
											target="_blank"
											rel="noopener noreferrer"
											className="solscan-link"
										>
											View on Solscan <ExternalLinkIcon />
										</a>
									)}
									{phase === "error" && (error || "Please try again")}
								</span>
							</div>
						</div>

						<div className="confirm-fee-rows">
							<FeeRow label="Rate">
								<span>
									1 {fromToken.symbol} ≈{" "}
									{fmtNumber(fromAmount > 0 ? toAmount / fromAmount : 0, 6)} {toToken.symbol}
								</span>
							</FeeRow>
							<FeeRow label="Price impact">
								<span
									style={{
										color:
											parseFloat(quote.priceImpactPct) > 1
												? "var(--danger)"
												: "inherit",
									}}
								>
									{formatPriceImpact(quote.priceImpactPct)}
								</span>
							</FeeRow>
							{minReceived !== null && (
								<FeeRow
									label="Min received"
									value={`${fmtNumber(minReceived, 6)} ${toToken.symbol}`}
								/>
							)}
							<FeeRow label="Slippage" value="0.5%" />
							<FeeRow label="Network fee">
								<GaslessFeeDisplay feeSol={feeSol} isLoading={isFeeLoading} />
							</FeeRow>
						</div>
					</div>

					<div className="confirm-actions">
						<button
							className="btn-secondary"
							onClick={() => onOpenChange(false)}
							disabled={isLoading}
						>
							{phase === "success" ? "Close" : "Cancel"}
						</button>
						{phase !== "success" && phase !== "error" && (
							<button className="btn-confirm" onClick={() => void handleConfirm()} disabled={isLoading}>
								{isLoading ? "Processing…" : "Confirm swap"}
							</button>
						)}
						{phase === "error" && (
							<button className="btn-confirm" onClick={() => void handleConfirm()}>
								Retry
							</button>
						)}
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

function FeeRow({
	label,
	value,
	children,
}: {
	label: string;
	value?: string;
	children?: React.ReactNode;
}) {
	return (
		<div className="fee-row">
			<span style={{ color: "var(--text-muted)" }}>{label}</span>
			<span style={{ fontWeight: 500 }}>{children ?? value}</span>
		</div>
	);
}
